import { useQuery } from "@apollo/client";
import { GetTenantQuery } from "generated-graphql/graphql";
import { isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSessionStorageState from "use-session-storage-state";
import { gql } from "../generated-graphql";

gql(`
  fragment tenant on Tenant {
    id
    name
    isDisabled
    isDemo
    isPartner
    partnerTenantId
  }
`);

const GET_TENANT_QUERY = gql(`
  query GetTenant($tenantId: ID!) {
    tenant(id: $tenantId) {
      ...tenant
      featureFlags
    }
  }
`);

export type Tenant = GetTenantQuery["tenant"] | undefined;

const TENANT_SESSION_STORAGE_KEY = "savedTenant";

export function useTenantIdPathParam() {
  const pathParts = window.location.pathname.split("/");
  const tenantIndex = pathParts.indexOf("o");
  return tenantIndex !== -1 ? pathParts[tenantIndex + 1] : undefined;
}

/**
 * Returns the effective tenant id for the current context.
 * Prefers the tenant id from the URL path if present; otherwise falls back
 * to the session-stored selected tenant.
 */
export function useTenantId(): string | undefined {
  const pathTenantId = useTenantIdPathParam();
  const [savedTenant] = useSessionStorageState<Tenant>(
    TENANT_SESSION_STORAGE_KEY
  );
  return pathTenantId ?? savedTenant?.id;
}

export function useTenant() {
  const tenantIdPathParam = useTenantIdPathParam();

  const [savedTenant, setSavedTenant] = useSessionStorageState<Tenant>(
    TENANT_SESSION_STORAGE_KEY
  );

  const sessionTenantId = tenantIdPathParam ?? savedTenant?.id;

  const [internalTenantId, setInternalTenantId] = useState<string | undefined>(
    sessionTenantId
  );

  useEffect(() => {
    setInternalTenantId(sessionTenantId);
  }, [sessionTenantId]);

  const { data, loading, error } = useQuery(GET_TENANT_QUERY, {
    variables: {
      tenantId: internalTenantId ?? "",
    },
    skip: isNil(internalTenantId),
    onCompleted(data) {
      setSavedTenant(data.tenant);
    },
  });

  const setCurrentTenantId = useCallback(
    async (newTenantId: string | null | undefined) => {
      if (newTenantId && newTenantId !== internalTenantId) {
        setInternalTenantId(newTenantId);
      }
    },
    [internalTenantId]
  );

  const tenant = useMemo(() => {
    return data?.tenant ?? savedTenant;
  }, [data?.tenant, savedTenant]);

  const isAmazon = internalTenantId === "6a92e397-bb74-44ed-afee-f84c7821f40e";

  return {
    tenantId: internalTenantId,
    isAmazon,
    tenant: tenant?.id === internalTenantId ? tenant : undefined,
    loading,
    error,
    setCurrentTenantId,
  };
}
